<template>
  <div class="py-4 container-fluid">
    <!-- Print Button -->
    <div class="row mb-2 no-print">
      <div class="col">
        <button @click="printReport" class="btn btn-primary mb-3">
          <i class="fas fa-print"></i> Print Report
        </button>
      </div>
    </div>

    <!-- Printable Area Start -->
    <div class="printable-area">
      <!-- Company Header -->
      <div class="company-header">
        <div class="company-logo">
          <h1>{{ company.company_name || "Company Name" }}</h1>
        </div>
        <div class="company-details">
          <p v-if="company.address">{{ company.address }}</p>
          <p v-if="company.phone">Phone: {{ company.phone }}</p>
          <p v-if="company.email">Email: {{ company.email }}</p>
        </div>
        <hr class="company-divider" />
        <h2 class="report-title">Stocktake Session Report</h2>
      </div>

      <!-- Session Details -->
      <div class="session-details">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Session Name:</span>
            <span class="detail-value">{{ session_name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Store:</span>
            <span class="detail-value">{{ store_name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Status:</span>
            <span
              class="status-badge"
              :class="
                status === 'Active' || status === 'active'
                  ? 'status-active'
                  : 'status-inactive'
              "
            >
              {{ status }}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Operator ID:</span>
            <span class="detail-value">{{ user_id }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Operator Name:</span>
            <span class="detail-value">{{ user_name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Started:</span>
            <span class="detail-value">{{ form_date(start_datetime) }}</span>
          </div>
        </div>
      </div>

      <!-- Stocktake Data Table -->
      <div class="data-table-container mt-4">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 30%">Item Name</th>
              <th style="width: 15%">System Quantity</th>
              <th style="width: 15%">Shelf Quantity</th>
              <th style="width: 10%">Variance</th>
              <th style="width: 15%">Variance Equivalence</th>
              <th style="width: 10%">Time Snapshot</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in stockTakeData" :key="entry.id">
              <td>{{ index + 1 }}</td>
              <td>
                <ItemName
                  :ItemName="entry.item_name"
                  :ItemBrand="entry.brand_name"
                  :ItemStrength="entry.item_strength"
                />
              </td>
              <td class="text-center">{{ entry.quantity_expected }}</td>
              <td class="text-center">{{ entry.quantity_counted }}</td>
              <td class="text-center" :class="getVarianceClass(entry.variance)">
                {{ entry.variance }}
              </td>
              <td class="text-right">
                {{ formatCurrency(entry.variance_equivalent) }}
              </td>
              <td>{{ form_date(entry.created_at) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td colspan="2"><strong>Totals</strong></td>
              <td class="text-center">
                <strong>{{ totalSystemQuantity }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ totalShelfQuantity }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ totalVariance }}</strong>
              </td>
              <td class="text-right">
                <strong>{{ formatCurrency(totalVarianceEquivalence) }}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Footer -->
      <div class="report-footer">
        <div class="footer-grid">
          <div class="footer-item">
            <p>Prepared by: _________________________</p>
            <p class="footer-subtitle">{{ user_name }}</p>
          </div>
          <div class="footer-item">
            <p>Checked by: _________________________</p>
            <p class="footer-subtitle">Date: _______________</p>
          </div>
          <div class="footer-item">
            <p>Approved by: _________________________</p>
            <p class="footer-subtitle">Date: _______________</p>
          </div>
        </div>
        <div class="print-date">
          <p>Printed on: {{ currentDateTime }}</p>
        </div>
      </div>
    </div>
    <!-- Printable Area End -->
  </div>
</template>

<script>
import { generalApiRequest } from "../../api/apiIndex";
import ItemName from "./components/ItemName.vue";
import formatDateTime from "../../timezone/utc_local_time";
import showNotification from "../../notification/notify";

export default {
  name: "StocktakeReport",
  data() {
    return {
      session: {},
      itemName: "",
      itemId: null,
      systemQty: "",
      shelfQty: "",
      variance: "",
      varianceValue: "",
      sellingPrice: "",
      store_id: this.$route.params.storeLocation,
      session_id: this.$route.params.sessionId,
      session_name: "",
      store_name: "",
      stockTakeData: [],
      start_datetime: "",
      status: "",
      user_id: "",
      user_name: "",
      company: {},
      currentDateTime: "",
    };
  },
  components: {
    ItemName,
  },
  computed: {
    totalSystemQuantity() {
      return this.stockTakeData.reduce(
        (sum, item) => sum + (parseFloat(item.quantity_expected) || 0),
        0,
      );
    },
    totalShelfQuantity() {
      return this.stockTakeData.reduce(
        (sum, item) => sum + (parseFloat(item.quantity_counted) || 0),
        0,
      );
    },
    totalVariance() {
      return this.stockTakeData.reduce(
        (sum, item) => sum + (parseFloat(item.variance) || 0),
        0,
      );
    },
    totalVarianceEquivalence() {
      return this.stockTakeData.reduce(
        (sum, item) => sum + (parseFloat(item.variance_equivalent) || 0),
        0,
      );
    },
  },
  methods: {
    form_date(tm) {
      return formatDateTime(tm);
    },
    formatCurrency(value) {
      if (!value && value !== 0) return "";
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    getVarianceClass(variance) {
      if (!variance) return "";
      const num = parseFloat(variance);
      if (num > 0) return "variance-positive";
      if (num < 0) return "variance-negative";
      return "";
    },
    async fetchSessionDetails() {
      const res = await generalApiRequest(
        `stocktakes/${this.$route.params.sessionId}/items`,
        "GET",
        {},
        1,
      );

      if (res.status === "success") {
        this.stockTakeData = res.data;
        this.session_name = res.session_details.session_name;
        this.store_name = res.session_details.store_name;
        this.start_datetime = res.session_details.start_datetime;
        this.status = res.session_details.status;
        this.user_id = res.session_details.user_id;
        this.user_name = res.session_details.user_name;
        this.company = res.company;
        this.currentDateTime = formatDateTime(new Date());
      }
    },
    printReport() {
      window.print();
    },
    setItem(item) {
      this.itemId = item.id;
      this.systemQty = item.c_quantity;
    },
    calculateVariance() {
      if (this.shelfQty === "" || this.systemQty === "") return;
      this.variance = this.shelfQty - this.systemQty;
      this.varianceValue = this.variance * this.sellingPrice;
    },
    async submitStocktakeItem() {
      if (!this.itemId || this.shelfQty === "") {
        alert("Fill all required fields.");
        return;
      }

      const payload = {
        session_id: this.session_id,
        product_id: this.itemId,
        quantity_expected: this.systemQty,
        quantity_counted: this.shelfQty,
        variance: this.variance,
        variance_equivalent: this.varianceValue,
        remarks: null,
        created_by: 1,
      };

      const res = await generalApiRequest(
        "stocktakes/add-item",
        "POST",
        payload,
        1,
      );

      if (res.status === "success") {
        this.itemName = "";
        this.itemId = null;
        this.systemQty = "";
        this.shelfQty = "";
        this.variance = "";
        this.varianceValue = "";
        this.sellingPrice = "";

        showNotification("Successfully added!");
        this.fetchSessionDetails();
      }
    },
    capitalize(str) {
      if (!str) return "";
      return str
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  mounted() {
    this.fetchSessionDetails();
  },
};
</script>

<style scoped>
/* ========== Print Styles ========== */
@media print {
  body * {
    visibility: hidden;
  }

  .printable-area,
  .printable-area * {
    visibility: visible;
  }

  .printable-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  .no-print {
    display: none !important;
  }

  @page {
    size: A4 landscape;
    margin: 1.5cm;
  }

  .company-header {
    text-align: center;
    margin-bottom: 20px;
  }

  .company-divider {
    border: 1px solid #000;
  }

  .data-table {
    border: 1px solid #000 !important;
  }

  .data-table th {
    background-color: #e9ecef !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .totals-row {
    background-color: #e9ecef !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .status-badge {
    border: 1px solid #000 !important;
  }

  .report-footer {
    margin-top: 30px;
    page-break-inside: avoid;
  }
}

/* ========== Screen Styles ========== */
.printable-area {
  background: white;
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Company Header */
.company-header {
  text-align: center;
  margin-bottom: 25px;
}

.company-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #1a237e;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.company-details {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

.company-details p {
  margin: 3px 0;
}

.company-divider {
  border: 0;
  border-top: 2px solid #1a237e;
  margin: 15px 0;
  width: 100%;
}

.report-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 15px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Session Details */
.session-details {
  margin-bottom: 25px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.detail-label {
  font-weight: 600;
  color: #555;
  margin-right: 8px;
  min-width: 120px;
}

.detail-value {
  color: #333;
}

/* Status Badge */
.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-inactive {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Data Table */
.data-table-container {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 30px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background-color: white;
  color: black;
}

.data-table thead th {
  background-color: #1a237e;
  color: white;
  padding: 12px 8px;
  border: 1px solid #ddd;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-align: left;
}

.data-table tbody td {
  padding: 8px;
  border: 1px solid #ddd;
  vertical-align: middle;
}

.data-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.data-table tbody tr:hover {
  background-color: #e3f2fd;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

/* Variance Colors */
.variance-positive {
  color: #28a745;
  font-weight: 600;
}

.variance-negative {
  color: #dc3545;
  font-weight: 600;
}

/* Totals Row */
.totals-row {
  background-color: #e9ecef;
  border-top: 2px solid #1a237e;
}

.totals-row td {
  padding: 12px 8px;
  font-size: 14px;
}

/* Report Footer */
.report-footer {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.footer-item {
  text-align: center;
}

.footer-item p {
  margin: 5px 0;
  font-size: 14px;
}

.footer-subtitle {
  color: #666;
  font-size: 12px;
}

.print-date {
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 20px;
}

/* Print Button */
.no-print {
  text-align: right;
  margin-bottom: 20px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  padding: 10px 20px;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.3s;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.fa-print {
  margin-right: 8px;
}
</style>
